import {
    type ApiContainerAction,
    type ApiContainerStockEntry,
    type ApiPlannedContainerAction,
    type EnumContainerActionRequest,
    EnumContainerOrderAction,
    isBooked,
} from '@Model';
import { actionKindOf, type ContainerActionKind } from './containerActionKind';
import { type ContainerStockGroup, type ContainerStockRow, groupStock, toRows } from './containerStockGroups';
import {
    alikeKey,
    claimRows,
    groupAlike,
    isConfirmedRow,
    isOfOwnOrder,
    isOpenFollowUp,
    kindOfRow,
    otherOrderTakesFromStock,
    type OwnOrder,
    rowFollowsByNumber,
    rowFollowsByTypeAndContent,
    rowTakesContainer,
    rowTakesFromPool,
    rowTakesLine,
    takesAway,
} from './siteMatching';
import { type ContainerTypeDetailsByTypeId } from './useContainerTypeDetails';

/** One card of the stock at the site: a single container, or a pooled type with its count. */
export interface StockCard {
    key: string;
    group: ContainerStockGroup;
    isPool: boolean;
    followUps: EnumContainerActionRequest[];
    /** What this order already does with the card's containers, as soon as it takes one. */
    handledInPresentOrder: ContainerActionKind | undefined;
    /** How many of the card's containers this order takes. */
    takenInPresentOrder: number;
    /** How many of those rows are confirmed as carried out and get booked on saving. */
    confirmedInPresentOrder: number;
    /** How many of the card's containers this order picks up or swaps out; an emptying leaves them standing. */
    removedInPresentOrder: number;
    /** Whether this order takes every container of the card. */
    isFullyHandled: boolean;
    /** The standing times this order has not taken yet, oldest first; empty once it took all. */
    openStandingTimeIds: string[];
    /** The rows of this order the card accounts for, at most one per container standing. */
    takenRowIds: string[];
}

/** A placement another open order plans at the site, which a pickup can be planned for ahead. */
export interface ComingCard {
    key: string;
    /** The first of the alike placements. */
    planned: ApiPlannedContainerAction;
    /** All alike placements; one request plans a follow-up for each. */
    alike: ApiPlannedContainerAction[];
    /** How many alike containers the order brings. */
    count: number;
    /** What this order already does with the coming containers, matched by number or else by type and content. */
    handledInPresentOrder: ContainerActionKind | undefined;
    /** How many of the coming containers this order takes. */
    takenInPresentOrder: number;
    /** How many of them this order picks up or swaps out; an emptying leaves them standing. */
    removedInPresentOrder: number;
    /** Whether this order takes every coming container of the card. */
    isFullyHandled: boolean;
    /** Whether the containers come as swaps that take away a container of the same type, so they add none. */
    replacesSameType: boolean;
}

/** The cards of the containers standing at the site, oldest first within a pooled type. */
// @ai-generated 2026-10-15T12:08:29+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
export function toStockCards(
    standing: ApiContainerStockEntry[],
    types: ContainerTypeDetailsByTypeId,
    actions: ApiContainerAction[],
): StockCard[] {
    const handling = actions.filter(isOpenFollowUp);

    return groupStock(toRows(standing)).flatMap((group) =>
        isIndividual(group, types)
            ? group.lines.map((line) => individualCard(groupStock([line])[0], line.standing_time_id, handling))
            : groupStock(group.lines, true).map((alike) => poolCard(alike, handling)),
    );
}

/** A container this order brings to the site and has not booked yet. */
export interface OwnPlacementCard {
    key: string;
    /** The first of the alike placements. */
    action: ApiContainerAction;
    /** How many alike containers this order brings. */
    count: number;
    /** How many of them are confirmed as carried out and get booked on saving. */
    confirmedCount: number;
}

/** The placements of this order that are not booked yet, read from its rows so they show while editing. */
// @ai-generated 2026-10-15T12:08:29+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
export function toOwnPlacementCards(actions: ApiContainerAction[]): OwnPlacementCard[] {
    const placements = actions.filter(
        (action) => action.action === EnumContainerOrderAction.Place && !isBooked(action.booking_status),
    );

    return groupAlike(placements, alikeKey).map((alike) => ({
        key: alike[0].id,
        action: alike[0],
        count: alike.length,
        confirmedCount: alike.filter(isConfirmedRow).length,
    }));
}

/**
 * The placements other orders plan at the site; this order's own are left out, by number too for its edit draft.
 * Rows of this order that no stock card accounts for are matched to them, best effort, by number and else by type
 * and content, each to the first card it fits.
 */
// @ai-generated 2026-10-15T12:08:29+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
export function toComingCards(
    planned: ApiPlannedContainerAction[],
    order: OwnOrder,
    actions: ApiContainerAction[],
    stockCards: StockCard[],
): ComingCard[] {
    const coming = planned.filter((action) => isPlacement(action) && !isOfOwnOrder(action, order));
    const groups = groupAlike(coming, (action) => `${action.source}|${action.source_id}|${alikeKey(action)}`);
    const atTheSite = new Set(stockCards.flatMap((card) => card.takenRowIds));
    const open = actions.filter((action) => isOpenFollowUp(action) && !atTheSite.has(action.id));
    const counts = groups.map((alike) => alike.reduce((sum, action) => sum + (action.count ?? 1), 0));
    const pickedUpTypes = new Map(
        planned
            .filter((action) => action.exchange_id && !isPlacement(action))
            .map((action) => [action.exchange_id, action.container_type?.foreign_key]),
    );
    const byNumber = groups.map((alike, index) => claimRows(open, alike[0], counts[index], rowFollowsByNumber));
    const byTypeAndContent = groups.map((alike, index) =>
        claimRows(open, alike[0], counts[index], rowFollowsByTypeAndContent),
    );

    return groups.map((alike, index) => {
        const taken = [...byNumber[index], ...byTypeAndContent[index]];
        const count = counts[index];

        return {
            key: alike[0].id,
            planned: alike[0],
            alike,
            count,
            handledInPresentOrder: taken.length > 0 ? kindOfRow(taken[0]) : undefined,
            takenInPresentOrder: taken.length,
            removedInPresentOrder: taken.filter(takesAway).length,
            isFullyHandled: taken.length > 0 && taken.length >= count,
            replacesSameType: alike.every(
                (action) =>
                    !!action.exchange_id &&
                    pickedUpTypes.get(action.exchange_id) === action.container_type?.foreign_key,
            ),
        };
    });
}

const isPlacement = (action: ApiPlannedContainerAction) => action.action === EnumContainerOrderAction.Place;

const isIndividual = (group: ContainerStockGroup, types: ContainerTypeDetailsByTypeId) =>
    types.get(group.container_type.foreign_key ?? '')?.usesIndividualManagement === true;

// @ai-generated 2026-10-15T12:08:29+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
function individualCard(group: ContainerStockGroup, key: string, handling: ApiContainerAction[]): StockCard {
    const containerId = group.lines[0].container.foreign_key;
    const handler = handling.find((action) => rowTakesContainer(action, containerId));

    return {
        key,
        group,
        isPool: false,
        followUps: group.lines[0].allowed_follow_ups,
        handledInPresentOrder: handler && kindOfRow(handler),
        takenInPresentOrder: handler ? 1 : 0,
        confirmedInPresentOrder: handler && isConfirmedRow(handler) ? 1 : 0,
        removedInPresentOrder: handler && takesAway(handler) ? 1 : 0,
        isFullyHandled: !!handler,
        openStandingTimeIds: handler ? [] : [key],
        takenRowIds: handler ? [handler.id] : [],
    };
}

// @ai-generated 2026-10-15T12:08:29+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
function poolCard(group: ContainerStockGroup, handling: ApiContainerAction[]): StockCard {
    const taken = handling.filter((action) => rowTakesFromPool(action, group));

    return {
        key: group.id,
        group,
        isPool: true,
        followUps: group.lines[0].allowed_follow_ups,
        handledInPresentOrder: taken.length > 0 ? kindOfRow(taken[0]) : undefined,
        takenInPresentOrder: taken.length,
        confirmedInPresentOrder: taken.filter(isConfirmedRow).length,
        removedInPresentOrder: taken.filter(takesAway).length,
        isFullyHandled: taken.length >= group.count,
        openStandingTimeIds: untaken(group.lines, taken),
        takenRowIds: taken.slice(0, group.count).map((action) => action.id),
    };
}

// @ai-generated 2026-10-21T06:59:59+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
function untaken(lines: readonly ContainerStockRow[], taken: readonly ApiContainerAction[]): string[] {
    const open = [...lines];
    const unmatched = taken.filter((action) => {
        const index = open.findIndex((line) => rowTakesLine(action, line));
        if (index < 0) {
            return true;
        }

        open.splice(index, 1);
        return false;
    });

    return open.slice(unmatched.length).map((line) => line.standing_time_id);
}

/** What other open orders already plan for the containers of a card. */
export interface PlannedElsewhere {
    /** The orders that plan for the card's containers, each once with how many of them it takes. */
    orders: { kind: ContainerActionKind; orderId: string; number: string; count: number }[];
    /** How many of the card's containers other orders plan for. */
    count: number;
    /** How many of them other orders pick up or swap out. */
    removedCount: number;
}

/** The pickups, swaps and emptyings other orders plan for a card's containers; this order and non-orders stay out. */
// @ai-generated 2026-10-15T12:08:29+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
export function plannedElsewhereOf(
    card: StockCard,
    takings: readonly ApiPlannedContainerAction[],
    order: OwnOrder,
): PlannedElsewhere {
    const matching = takings.filter((taking) => otherOrderTakesFromStock(taking, card.group, card.isPool, order));

    const orders = new Map<string, PlannedElsewhere['orders'][number]>();
    for (const taking of matching) {
        const known = orders.get(taking.source_id);
        orders.set(taking.source_id, {
            kind: known?.kind ?? (taking.exchange_id ? 'swap' : actionKindOf(taking.action)),
            orderId: taking.source_id,
            number: taking.source_number,
            count: (known?.count ?? 0) + taking.count,
        });
    }

    return {
        orders: [...orders.values()],
        count: matching.reduce((sum, taking) => sum + taking.count, 0),
        removedCount: matching.filter(takesAway).reduce((sum, taking) => sum + taking.count, 0),
    };
}

/** Another order the sidebar links to. */
export interface OtherOrder {
    orderId: string;
    number: string;
}
